// Copyright Materialize, Inc. and contributors. All rights reserved.
//
// Use of this software is governed by the Business Source License
// included in the LICENSE file.
//
// As of the Change Date specified in that file, in accordance with
// the Business Source License, use of this software will be governed
// by the Apache License, Version 2.0.

import { greatest, max } from "d3";
import { millisecondsInMinute } from "date-fns/constants";

import { SizeAllocation } from "~/api/materialize/cluster/replicaProcessMetrics";
import { OOM_REASON } from "~/api/materialize/cluster/replicaStatusTimeline";

/**
 * The database records no cause for a hydration, so one starting this close to
 * the replica coming online or being created counts as a full hydration.
 */
export const BOOT_WINDOW_MS = 2 * millisecondsInMinute;

/** End events of a replica this close together are one restart. */
const RESTART_MERGE_MS = millisecondsInMinute;

export interface HydrationReplica {
  id: string;
  name: string;
  size: string | undefined;
  isCurrent: boolean;
  createdAt: Date | undefined;
  droppedAt: Date | undefined;
}

/**
 * Whether a hydration rebuilt everything on a booting replica, or only
 * objects added to a replica that was already hydrated.
 */
export type HydrationKind = "full" | "additive";

/** One finished hydration episode of a replica. */
export interface Hydration {
  /** The replica and start, which identify an episode. */
  id: string;
  replicaId: string;
  startedAt: Date;
  finishedAt: Date;
  durationMs: number;
  objectCount: number;
  /** The highest memory plus disk of one process since it started. */
  peakHeapBytes: number | null;
  peakMemoryBytes: number | null;
  peakDiskBytes: number | null;
  kind: HydrationKind;
}

/** A replica that went offline before it finished hydrating. */
export interface UnfinishedHydration {
  id: string;
  replicaId: string;
  /** When the replica came online for this attempt, if it was recorded. */
  startedAt: Date | undefined;
  endedAt: Date;
  /** Why the replica went offline, such as `oom-killed`. */
  reason: string | null;
}

export interface ReplicaTransition {
  replicaId: string;
  occurredAt: Date;
  status: string;
  reason: string | null;
}

/**
 * The cluster's current replicas, then replicas from its history dropped at or
 * after `startMs`, newest first. Current replicas keep their current name and
 * size.
 */
export function mergeHydrationReplicas(
  current: Array<{ id: string; name: string; size: string | null }>,
  history: Array<{
    replicaId: string | null;
    name: string | null;
    size: string | null;
    createdAt: Date;
    droppedAt: Date | null;
  }>,
  startMs: number,
): HydrationReplica[] {
  const historyById = new Map(
    history.flatMap((row) => (row.replicaId ? [[row.replicaId, row]] : [])),
  );
  const currentIds = new Set(current.map(({ id }) => id));
  const currentReplicas = current.map(({ id, name, size }) => ({
    id,
    name,
    size: size ?? undefined,
    isCurrent: true,
    createdAt: historyById.get(id)?.createdAt,
    droppedAt: undefined,
  }));
  // The history is read once per set of current replicas, so it can still hold
  // replicas dropped before a window that has since moved on.
  const dropped = [...historyById.entries()]
    .filter(
      ([id, row]) =>
        !currentIds.has(id) &&
        (row.droppedAt === null || row.droppedAt.getTime() >= startMs),
    )
    .map(([id, row]) => ({
      id,
      name: row.name ?? id,
      size: row.size ?? undefined,
      isCurrent: false,
      createdAt: row.createdAt,
      droppedAt: row.droppedAt ?? undefined,
    }))
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  return [...currentReplicas, ...dropped];
}

/** When each replica booted: its creation and each time it came online. */
export function replicaBootTimes(
  transitions: ReplicaTransition[],
  replicas: HydrationReplica[],
) {
  const boots = new Map<string, number[]>();
  const add = (replicaId: string, ms: number) =>
    boots.set(replicaId, [...(boots.get(replicaId) ?? []), ms]);
  for (const { id, createdAt } of replicas) {
    if (createdAt) add(id, createdAt.getTime());
  }
  for (const { replicaId, status, occurredAt } of transitions) {
    if (status === "online") add(replicaId, occurredAt.getTime());
  }
  for (const times of boots.values()) times.sort((a, b) => a - b);
  return boots;
}

/**
 * Hydration episode rows as hydrations, oldest first, each with its kind. A
 * hydration is full when it begins near a boot, or covers at least the objects
 * the cluster had when it finished: a zero-downtime upgrade rehydrates on new
 * processes while the environment is read-only, which records no boot.
 * `objectCountAt` is undefined while the cluster's objects are unknown.
 */
export function transformHydrations(
  rows: Array<{
    replicaId: string;
    startedAt: Date;
    finishedAt: Date;
    objectCount: number;
    peakHeapBytes: number | null;
    peakMemoryBytes: number | null;
    peakDiskBytes: number | null;
  }>,
  bootTimes: Map<string, number[]>,
  objectCountAt?: (ms: number) => number,
): Hydration[] {
  return rows
    .map((row) => {
      const startMs = row.startedAt.getTime();
      const isBoot = (bootTimes.get(row.replicaId) ?? []).some(
        (bootMs) => Math.abs(bootMs - startMs) <= BOOT_WINDOW_MS,
      );
      // Objects created while it ran raise the count it has to reach as much
      // as they raise its own, so a burst of new objects isn't full.
      const coversAll =
        objectCountAt !== undefined &&
        row.objectCount > 0 &&
        row.objectCount >= objectCountAt(row.finishedAt.getTime());
      return {
        ...row,
        id: `${row.replicaId}|${startMs}`,
        durationMs: row.finishedAt.getTime() - startMs,
        kind: isBoot || coversAll ? "full" : "additive",
      } satisfies Hydration;
    })
    .sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
}

/**
 * Times a replica went offline, or a process was killed for running out of
 * memory, oldest first. A replica going offline when it's dropped doesn't
 * count.
 */
export function findRestarts({
  transitions,
  oomKills,
  replicas,
  bootTimes,
}: {
  /** Process 0's transitions, which stand for the replica's. */
  transitions: ReplicaTransition[];
  /** OOM kills of any process. */
  oomKills: ReplicaTransition[];
  replicas: HydrationReplica[];
  bootTimes: Map<string, number[]>;
}): ReplicaTransition[] {
  const replicasById = new Map(
    replicas.map((replica) => [replica.id, replica]),
  );
  // On Kubernetes, a new replica's pod records an offline row before it first
  // comes online, so only an offline row after an online one is a restart.
  const wentOffline: ReplicaTransition[] = [];
  const lastStatusById = new Map<string, string>();
  for (const transition of [...transitions].sort(
    (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime(),
  )) {
    if (
      transition.status === "offline" &&
      lastStatusById.get(transition.replicaId) === "online"
    ) {
      wentOffline.push(transition);
    }
    lastStatusById.set(transition.replicaId, transition.status);
  }
  const ends = [...wentOffline, ...oomKills].sort(
    (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime(),
  );

  // One restart can be recorded as process 0 going offline and as OOM kills
  // on several processes. Keep the first, with an OOM reason if any has one.
  // An end after the replica came back online is a restart of its own.
  const isSameRestart = (
    previous: ReplicaTransition,
    end: ReplicaTransition,
  ) => {
    const previousMs = previous.occurredAt.getTime();
    const endMs = end.occurredAt.getTime();
    return (
      endMs - previousMs <= RESTART_MERGE_MS &&
      !(bootTimes.get(end.replicaId) ?? []).some(
        (bootMs) => bootMs > previousMs && bootMs < endMs,
      )
    );
  };
  const restarts: ReplicaTransition[] = [];
  for (const end of ends) {
    const previous = restarts.findLast(
      ({ replicaId }) => replicaId === end.replicaId,
    );
    if (previous && isSameRestart(previous, end)) {
      if (end.reason === OOM_REASON) previous.reason = OOM_REASON;
      continue;
    }
    restarts.push({ ...end });
  }
  return restarts.filter(({ replicaId, occurredAt, reason }) => {
    const droppedMs = replicasById.get(replicaId)?.droppedAt?.getTime();
    return (
      reason === OOM_REASON ||
      droppedMs === undefined ||
      occurredAt.getTime() < droppedMs - RESTART_MERGE_MS
    );
  });
}

export interface ObjectLifetime {
  createdAtMs: number;
  droppedAtMs?: number;
}

/** Identifies a restart, and the unfinished hydration it ended if any. */
const restartId = ({ replicaId, occurredAt }: ReplicaTransition) =>
  `${replicaId}|${occurredAt.getTime()}`;

/**
 * The restarts from `startMs` on that came before the replica finished
 * hydrating.
 *
 * An attempt runs from the replica's previous restart, or its creation, to the
 * restart. Online rows don't bound it: the one a zero-downtime upgrade writes
 * comes after the replica already hydrated. An object created during the
 * attempt starts a hydration of its own, so the attempt finished only if a
 * hydration finished after the last such object. The hydrations before
 * `startMs` aren't read, so an attempt that began before it is skipped unless
 * such an object was created after it.
 *
 * NOTE: the collector visits one replica per interval, so a hydration that
 * finished before a restart and before the replica's next visit is never
 * recorded, and that restart reads as unfinished.
 */
export function findUnfinishedHydrations({
  restarts,
  hydrations,
  replicas,
  bootTimes,
  objectLifetimes,
  startMs,
}: {
  /** Every restart of the replicas, as `findRestarts` returns them. */
  restarts: ReplicaTransition[];
  /** Every finished hydration, including ones with no objects. */
  hydrations: Hydration[];
  replicas: HydrationReplica[];
  bootTimes: Map<string, number[]>;
  /** When the cluster's objects were created, and dropped if they were. */
  objectLifetimes: ObjectLifetime[];
  startMs: number;
}): UnfinishedHydration[] {
  const replicasById = new Map(
    replicas.map((replica) => [replica.id, replica]),
  );
  return restarts.flatMap((restart, index) => {
    const { replicaId, occurredAt, reason } = restart;
    const endMs = occurredAt.getTime();
    if (endMs < startMs) return [];
    const replica = replicasById.get(replicaId);
    const previousRestartMs = restarts
      .slice(0, index)
      .findLast((previous) => previous.replicaId === replicaId)
      ?.occurredAt.getTime();
    const attemptStartMs =
      previousRestartMs ?? replica?.createdAt?.getTime() ?? -Infinity;
    // An object dropped before the restart wasn't hydrating at it. Dropping an
    // unfinished object records no finished hydration, so it would otherwise
    // read as cut short.
    const lastCreatedMs =
      max(objectLifetimes, ({ createdAtMs, droppedAtMs }) =>
        createdAtMs > attemptStartMs &&
        createdAtMs < endMs &&
        (droppedAtMs === undefined || droppedAtMs > endMs)
          ? createdAtMs
          : undefined,
      ) ?? attemptStartMs;
    if (lastCreatedMs < startMs) return [];
    const finishedBetween = (fromMs: number, toMs: number) =>
      hydrations.some(
        (hydration) =>
          hydration.replicaId === replicaId &&
          hydration.finishedAt.getTime() > fromMs &&
          hydration.finishedAt.getTime() <= toMs,
      );
    if (finishedBetween(lastCreatedMs, endMs)) return [];
    // A replica that had finished before the object was created was hydrating
    // only that object, which is all that's known of an attempt that began
    // before the window. Otherwise it was hydrating since it came online for
    // this attempt, if that was recorded.
    const hydratingSinceMs =
      attemptStartMs < startMs || finishedBetween(attemptStartMs, lastCreatedMs)
        ? lastCreatedMs
        : ((bootTimes.get(replicaId) ?? []).findLast(
            (ms) => ms >= attemptStartMs && ms < endMs,
          ) ?? attemptStartMs);
    return [
      {
        id: restartId(restart),
        replicaId,
        startedAt: new Date(hydratingSinceMs),
        endedAt: occurredAt,
        reason,
      },
    ];
  });
}

/**
 * The restarts from `startMs` on that read as finished. One can still have
 * cut a hydration short if the replica was hydrating an object missing from
 * the creation times, such as one dropped since.
 */
export function restartsReadingAsFinished(
  restarts: ReplicaTransition[],
  unfinished: UnfinishedHydration[],
  startMs: number,
) {
  const unfinishedIds = new Set(unfinished.map(({ id }) => id));
  return restarts.filter(
    (restart) =>
      restart.occurredAt.getTime() >= startMs &&
      !unfinishedIds.has(restartId(restart)),
  );
}

/** When a replica last booted: its creation or the last time it came online. */
export const latestBoot = (
  bootTimes: Map<string, number[]>,
  replicaId: string,
) => {
  const ms = bootTimes.get(replicaId)?.at(-1);
  return ms === undefined ? undefined : new Date(ms);
};

/**
 * The full hydration that finished last, of `replicaId` or of any replica.
 * Hydrations are ordered by start, and one that started later can finish first.
 */
export const latestFullHydration = (
  hydrations: Hydration[],
  replicaId?: string,
) =>
  greatest(
    hydrations.filter(
      (hydration) =>
        hydration.kind === "full" &&
        (replicaId === undefined || hydration.replicaId === replicaId),
    ),
    ({ finishedAt }) => finishedAt.getTime(),
  );

/**
 * A replica's per-process heap limit: the limit it reports, or its size's
 * memory plus disk for a replica that no longer reports.
 */
export const heapLimitBytes = (
  replica: HydrationReplica,
  reportedLimits: Map<string, number> | undefined,
  allocationsBySize: Map<string, SizeAllocation> | undefined,
) => {
  const reported = reportedLimits?.get(replica.id);
  if (reported !== undefined) return reported;
  const allocation = replica.size
    ? allocationsBySize?.get(replica.size)
    : undefined;
  return allocation
    ? allocation.memoryBytes + (allocation.diskBytes ?? 0)
    : undefined;
};

/** One object's hydration within an episode, in ms from the episode's start. */
export interface ObjectHydrationSpan {
  globalId: string;
  /** The catalog id, null once the object is dropped. */
  objectId: string | null;
  installedMs: number;
  /** When it got a hydration slot. Equals `installedMs` when not recorded. */
  startedMs: number;
  hydratedMs: number | undefined;
  /** Time spent waiting for a hydration slot. */
  queuedMs: number;
  workingMs: number | undefined;
}

/** Object hydration rows of an episode, in the order they started. */
export function transformObjectHydrationSpans(
  rows: Array<{
    globalId: string;
    objectId: string | null;
    installedAt: Date;
    startedAt: Date | null;
    hydratedAt: Date | null;
  }>,
  episodeStartedAt: Date,
): ObjectHydrationSpan[] {
  const origin = episodeStartedAt.getTime();
  return rows
    .map(({ globalId, objectId, installedAt, startedAt, hydratedAt }) => {
      const installedMs = installedAt.getTime() - origin;
      const startedMs = (startedAt ?? installedAt).getTime() - origin;
      const hydratedMs = hydratedAt ? hydratedAt.getTime() - origin : undefined;
      return {
        globalId,
        objectId,
        installedMs,
        startedMs,
        hydratedMs,
        queuedMs: startedMs - installedMs,
        workingMs:
          hydratedMs === undefined ? undefined : hydratedMs - startedMs,
      };
    })
    .sort((a, b) => a.startedMs - b.startedMs || a.installedMs - b.installedMs);
}
